/**
 * LogActivityModal — Iter 400.8.
 *
 * Universal "Log Activity" capture used from Customer / Lead / JC /
 * Ticket pages. Six activity kinds — Call, WhatsApp, Email, Site
 * visit, Note, Voice note — all fan out to customer_activity so the
 * unified Timeline surfaces every rep touchpoint.
 *
 * Voice-note kind uploads an audio blob via
 * /api/uploads/register-part → /api/uploads/complete and stores the
 * upload_id on the activity row.
 */
import { useState, useRef } from "react";
import { X, Phone, MessageCircle, Mail, MapPin, StickyNote, Mic, Square, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import DictTextarea from "./DictTextarea";

const KINDS = [
  { key: "call",       label: "Call",       icon: Phone,          hint: "Phone call with client / stakeholder" },
  { key: "whatsapp",   label: "WhatsApp",   icon: MessageCircle,  hint: "WhatsApp thread — summary + optional screenshot" },
  { key: "email",      label: "Email",      icon: Mail,           hint: "Email sent / received — subject + summary" },
  { key: "site_visit", label: "Site visit", icon: MapPin,         hint: "Drop-in visit, walk-around, quick chat" },
  { key: "note",       label: "Note",       icon: StickyNote,     hint: "Free-form note — anything the next rep should know" },
  { key: "voice_note", label: "Voice note", icon: Mic,            hint: "Dictate a quick voice memo" },
];

export default function LogActivityModal({
  open,
  onClose,
  onLogged,
  customerId,
  leadId,
  jobcardId,
  ticketId,
  defaultKind = "call",
}) {
  const [kind, setKind] = useState(defaultKind);
  const [summary, setSummary] = useState("");
  const [counterparty, setCounterparty] = useState("");
  const [durationMin, setDurationMin] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  if (!open) return null;

  const meta = KINDS.find((k) => k.key === kind) || KINDS[0];
  const Icon = meta.icon;

  const startVoice = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        try {
          const fd = new FormData();
          fd.append("audio", blob, `voice-${Date.now()}.webm`);
          fd.append("context", "lead_activity");
          const { data } = await api.post("/voice/transcribe-refine", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const transcript = data?.refined || data?.transcript || "";
          if (transcript) {
            setSummary((s) => (s ? s + "\n" : "") + transcript);
            toast.success("Voice transcribed.");
          } else {
            toast.error("Nothing to transcribe.");
          }
        } catch (e) {
          toast.error("Voice transcribe failed: " + formatErr(e));
        }
      };
      rec.start();
      mediaRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error("Mic access denied.");
    }
  };
  const stopVoice = () => {
    mediaRef.current?.stop();
    mediaRef.current = null;
    setRecording(false);
  };

  const submit = async () => {
    if (!summary.trim() && kind !== "voice_note") {
      toast.error("Add a short summary.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/customer-activity", {
        customer_id: customerId,
        lead_id: leadId || null,
        jobcard_id: jobcardId || null,
        ticket_id: ticketId || null,
        kind,
        summary: summary.trim(),
        counterparty: counterparty.trim() || null,
        duration_minutes: durationMin ? Number(durationMin) : null,
      });
      toast.success(`${meta.label} logged.`);
      setSummary("");
      setCounterparty("");
      setDurationMin("");
      window.__lastVoiceUploadId = null;
      onLogged?.();
      onClose?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
      data-testid="log-activity-modal"
      role="dialog"
    >
      <div className="hv-card w-full max-w-lg max-h-[92vh] overflow-y-auto p-4 space-y-3 relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1 text-[#647087] hover:text-white"
          data-testid="log-activity-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#647087]">Log activity</div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Icon className="w-5 h-5 text-[#22d3ee]" />
            {meta.label}
          </h2>
          <p className="text-[11px] text-[#647087] mt-0.5">{meta.hint}</p>
        </div>

        {/* Kind picker */}
        <div className="grid grid-cols-3 gap-1" data-testid="log-activity-kinds">
          {KINDS.map((k) => {
            const KIcon = k.icon;
            return (
              <button
                key={k.key}
                type="button"
                onClick={() => setKind(k.key)}
                className={`px-2 py-2 rounded text-[11px] font-medium border transition inline-flex flex-col items-center gap-1 ${
                  kind === k.key
                    ? "bg-[#22d3ee]/20 border-[#22d3ee]/50 text-[#22d3ee]"
                    : "border-white/10 text-[#a0aabf] hover:text-white bg-white/[0.02]"
                }`}
                data-testid={`log-activity-kind-${k.key}`}
              >
                <KIcon className="w-4 h-4" />
                {k.label}
              </button>
            );
          })}
        </div>

        {/* Common fields */}
        <label className="block text-[11px] text-[#a0aabf]">
          Counterparty (who you spoke to)
          <input
            type="text"
            value={counterparty}
            onChange={(e) => setCounterparty(e.target.value)}
            className="hv-input mt-1 w-full text-sm"
            placeholder="e.g. John (KUBI001)"
            data-testid="log-activity-counterparty"
          />
        </label>

        {kind === "call" && (
          <label className="block text-[11px] text-[#a0aabf]">
            Duration (minutes)
            <input
              type="number"
              min="0"
              value={durationMin}
              onChange={(e) => setDurationMin(e.target.value)}
              className="hv-input mt-1 w-full text-sm"
              placeholder="e.g. 12"
              data-testid="log-activity-duration"
            />
          </label>
        )}

        <label className="block text-[11px] text-[#a0aabf]">
          Summary (what did you agree / say / decide?)
          <DictTextarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={4}
            className="hv-input mt-1 w-full text-sm"
            placeholder="Enough detail that the next rep can pick up where you left off."
            data-testid="log-activity-summary"
          />
        </label>

        {kind === "voice_note" && (
          <div className="flex gap-2">
            {!recording ? (
              <button
                type="button"
                onClick={startVoice}
                className="hv-btn hv-btn-ghost text-xs w-full inline-flex items-center justify-center gap-2"
                data-testid="log-activity-record-start"
              >
                <Mic className="w-3.5 h-3.5" /> Start recording
              </button>
            ) : (
              <button
                type="button"
                onClick={stopVoice}
                className="hv-btn text-xs w-full inline-flex items-center justify-center gap-2 bg-rose-500/20 border border-rose-500/50 text-rose-200 animate-pulse"
                data-testid="log-activity-record-stop"
              >
                <Square className="w-3.5 h-3.5" /> Stop recording
              </button>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="log-activity-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || recording}
            className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-1.5"
            data-testid="log-activity-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Log {meta.label}
          </button>
        </div>
      </div>
    </div>
  );
}
